import { AutogenLayoutPayeeTotals } from "./autogen/AutogenLayoutPayeeTotals";
import { ClickEvent, DataSourceAction, DataSourceExecutionEvent } from "@mcleod/components";
import { RowDriverSettlementProfile } from "../../driver-settlement/src/models/ModelDriverSettlementProfile";
import { CommonTooltips } from "@mcleod/common";
import { PayeeData } from "../PayeeData";
import { RowOfficePayrollProfile } from "../../officepayroll/src/models/ModelOfficePayrollProfile";

export interface PayeeSelectedProps {
    payeeName: string;
    payeeId: string;
    payeeData: PayeeData;
    profileRow: RowOfficePayrollProfile | RowDriverSettlementProfile;
}

export class PayeeTotals extends AutogenLayoutPayeeTotals {
    public refresh?: (forceUpdate?: boolean) => void;
    public updateRefreshLabel?: () => void;
    public viewTimecardOnClick?: () => Promise<void>;
    public onRecurringsClick?: () => void;
    private _expanded = false;
    private profileRow: RowOfficePayrollProfile | RowDriverSettlementProfile;

    onLoad(): void | Promise<void> {
        this.labelViewTimecard.addUnderlineWhenMouseOver();
        this.labelTimeCardTotal.style.textDecoration = "underline";
    }

    get expanded(): boolean {
        return this._expanded;
    }

    set expanded(value: boolean) {
        this._expanded = value;
        this.panelContent.visible = value;
        this.panelNetPay.visible = value;
        this.labelPayeeId.visible = value;
        this.panelRecurrings.visible = value;
        this.imageExpand.rotation = value ? -90 : 90;
        this.width = value ? 400 : null;
        this.panelDriverTotals.paddingLeft = value ? 8 : 0;
    }

    /** This is an event handler for the onClick event of imageExpand. */
    imageExpandOnClick(event: ClickEvent) {
        this.expanded = !this.expanded;
        this.refresh?.();
        this.updateRefreshLabel?.();
    }

    /** This is an event handler for the afterExecution event of sourcePayeeTotals. */
    sourcePayeeTotalsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.hidePanelsFieldsWithNoData();
        }
    }

    hidePanelsFieldsWithNoData() {
        // List all panels that should be dynamically shown/hidden based on data
        const fieldPanels = [
            this.panelLiabilityInsurance,
            this.panelFederalWithholding,
            this.panelSocialSecurity,
            this.panelMedicare,
            this.panelStateWithholding,
            this.panelStateDisability,
            this.panelStateUnemployment,
            this.panelCountyWithholding,
            this.panelCityWithholding,
            this.panelTrailerRent,
            this.panelWorkersCompensation,
            this.panel401kContribution,
            this.panel401kCatchUp,
            this.panelRoth401kContribution,
            this.panelRoth401kCatchUp,
            this.panelPFML,
            this.panelOtherPayrollTax
        ];
        if (!this.activeRow) return;
        fieldPanels.forEach(panel => {
            let hasData = false;
            panel.forEveryChildComponent(child => {
                if (child.field && !this.activeRow.isNull(child.field)) {
                    hasData = true;
                }
            });
            panel.visible = hasData;
        });

        let layoutPath = "";
        if (this.profileRow._modelPath.includes("driver-settlement-profile")) {
            const payeeType = this.activeRow.get("payee_type");
            this.panelTimecard.visible = this.labelViewTimecard.visible = payeeType == "C" && this.profileRow?.isNull("bonus_code");
            layoutPath = "lme/settlement/TimecardTotalsQuickInfo";
        }
        else if (this.profileRow._modelPath.includes("office-payroll-profile")) {
            this.panelTimecard.visible = this.labelViewTimecard.visible = this.profileRow?.isNull("bonus_code");
            layoutPath = "lme/backoffice/officepayroll/TimecardTotalsQuickInfo";
        }
        if (this.panelTimecard.visible) {
            this.imageNeedsReview.visible = this.activeRow.getCurrency("timecard_total").isZero();
            CommonTooltips.setTooltipContentLayout(this.labelTimeCardTotal, this.activeRow.get("payee_id"), layoutPath);
        }
        this.panelWithholdings.visible = this.activeRow.get("payee_type") !== "O";
        this.panelPreviousAmountOwed.visible = this.activeRow.getBoolean("show_previous_amount", false);
    }

    /** This is an event handler for the onClick event of labelRefresh. */
    labelRefreshOnClick(event: ClickEvent) {
        this.refresh?.(true);
    }

    async searchData(props: PayeeSelectedProps, cutoffDate: Date, recurringDate: Date = null) {
        this.profileRow = props.profileRow;
        const settlements = props.payeeData.getReadyToPaySettlements();
        const adjustments = props.payeeData.getReadyToPayAdjustments();
        const settlementIds = settlements.map(row => row.get("record_id")).join(",");
        const adjustmentIds = adjustments.map(row => row.get("record_id")).join(",");
        await this.search({
            payee_ids: props.payeeId,
            settlement_ids: settlementIds,
            deduction_ids: adjustmentIds,
            cutoff_date: cutoffDate,
            recurring_date: recurringDate,
            currency_code: props.profileRow instanceof RowOfficePayrollProfile ? "USD" : props.profileRow.get("currency_type"),
            bonus_code: props.profileRow.get("bonus_code"),
            override_frequency: props.profileRow.get("override_frequency"),
            pay_frequency: props.profileRow.get("pay_frequency"),
            pay_rate_method: props.profileRow.get("pay_rate_method"),
            bonus_tax_method: props.profileRow.get("bonus_tax_method")
        });
    }

    async labelViewTimecardOnClick(event: ClickEvent) {
        this.labelViewTimecard.busy = true;
        await this.viewTimecardOnClick?.();
        this.labelViewTimecard.busy = false;
    }

    /** This is an event handler for the onClick event of buttonRecurringDetail. */
    buttonRecurringDetailOnClick(event: ClickEvent) {
        this.onRecurringsClick?.();
    }
}
